import { INIT_SCHEMA } from '@/components/Dashboard/initSchema';
import { useAdminContext } from '@/contexts/AdminContext';
import { useAuth } from '@/contexts/AuthContext';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { supabase } from '@common/api/authService';
import { useEffect, useRef } from 'react';

/**
 * 轮询检查 workspace 是否创建成功的 hook
 * 用于支付成功后检查 workspace 是否已经创建完成
 */
export const useWorkspacePolling = () => {
    const {
        workspaces, refreshWorkspaces, switchWorkspace
    } = useAuth();
    const { modal, dashboard } = useAdminContext();
    const {onboardingData, stopOnboarding} = useOnboarding();

    // 使用 ref 存储会变化的方法和值，避免闭包陷阱
    const refreshWorkspacesRef = useRef(refreshWorkspaces);
    const modalRef = useRef(modal);
    const switchWorkspaceRef = useRef(switchWorkspace);
    const workspacesRef = useRef(workspaces);
    const onboardingDataRef = useRef(onboardingData);
    const dashboardRef = useRef(dashboard);
    const stopOnboardingRef = useRef(stopOnboarding);

    // 同步更新 ref
    useEffect(() => {
        refreshWorkspacesRef.current = refreshWorkspaces;
        modalRef.current = modal;
        switchWorkspaceRef.current = switchWorkspace;
        workspacesRef.current = workspaces;
        onboardingDataRef.current = onboardingData;
        dashboardRef.current = dashboard;
        stopOnboardingRef.current = stopOnboarding;
    }, [
        refreshWorkspaces,
        modal,
        switchWorkspace,
        workspaces,
        onboardingData,
        dashboard,
        stopOnboarding
    ]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const payResult = urlParams.get('pay_result');
        const currentOnboardingData = onboardingDataRef.current;
        
        // 只有在支付成功且在 onboarding 流程中才启动轮询
        if (payResult === 'success' && currentOnboardingData.workspaceId) {
            let isActive = true;
            let iterationCount = 0;
            const maxIterations = 30; // 30 秒超时
            const workspaceId = currentOnboardingData.workspaceId;

            const poll = async () => {
                if (!isActive) return;

                try {
                    iterationCount++;

                    // 直接查询 Supabase 检查 workspace 是否存在
                    // 这样避免了 React State 更新滞后的问题，也避免了对 Context 的依赖
                    const { data: workspace } = await supabase
                        .from('workspaces')
                        .select('id')
                        .eq('id', workspaceId)
                        .maybeSingle();

                    if (workspace) {
                        // Workspace 创建成功
                        
                        // 1. 先刷新全局状态，确保 Context 里有这个 workspace
                        await refreshWorkspacesRef.current();

                        // 2. 创建初始 dashboard
                        try {
                            // 先检查是否已存在 dashboard（幂等性保护）
                            const { data: existingDashboard } = await supabase
                                .from('workspace_dashboard')
                                .select('id')
                                .eq('workspace_id', workspaceId)
                                .maybeSingle();

                            if (!existingDashboard) {
                                // 只有不存在时才创建
                                const sortKey = Date.now();
                                const newTitle = 'Business Overview';

                                const { error } = await supabase
                                    .from('workspace_dashboard')
                                    .insert([{
                                        workspace_id: workspaceId,
                                        title: newTitle,
                                        schema: INIT_SCHEMA,
                                        sort_key: sortKey
                                    }])
                                    .select()
                                    .single();

                                if (error) {
                                    console.error('Insert dashboard error:', error);
                                    // 如果是唯一约束冲突，可以忽略
                                }
                            } else {
                                console.log('Dashboard 已存在，跳过创建');
                            }
                        } catch (error) {
                            console.error('创建初始 dashboard 失败:', error);
                        }

                        // 3. 后续清理工作
                        if (isActive) {
                            stopOnboardingRef.current();
                            modalRef.current?.updateModal('welcome', { data: { checking: false } });
                            switchWorkspaceRef.current(workspaceId, { onboarding_success: 'true' });
                            window.history.replaceState({}, '', window.location.pathname);
                        }
                        return; // 结束轮询
                    } 
                    
                    // 如果还没创建成功且未超时，继续轮询
                    if (iterationCount < maxIterations && isActive) {
                        setTimeout(poll, 1000);
                    }
                } catch (error) {
                    console.error('轮询检查 workspace 时出错:', error);
                    // 出错也继续重试，直到超时
                    if (iterationCount < maxIterations && isActive) {
                        setTimeout(poll, 1000);
                    }
                }
            };

            // 启动轮询
            poll();

            return () => {
                isActive = false;
            };
        }
    }, []); // 空依赖数组，只在组件挂载时执行一次
};

